/**
 * WorldCupLeaderboard — Shared podium + ranked list component.
 *
 * Used by LeaderboardView (global, FlashList) and GroupDetailView (scroll, embedded).
 *
 * Layout: PodiumStage (top-3, Week/Overall toggle) above a list of ranks 4+,
 * with an optional pinned "me" footer for users off the top-N slice.
 */

import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import type { FlashListRef, ViewToken } from '@shopify/flash-list';
import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../../src/shared/constants/flashList.constants';
import { useTheme } from '../../../src/theme/useTheme';
import { StaggeredFadeIn } from '../../shared/StaggeredFadeIn';
import { Skeleton } from '../../ui/Skeleton/Skeleton';
import { LeaderboardCard } from './LeaderboardCard';
import { getWorldCupLeaderboardStyles } from './WorldCupLeaderboard.styles';
import { PodiumStage } from './PodiumStage';
import type { LeaderboardPeriod } from './PodiumStage.constants';
import { LeaderboardRow } from './LeaderboardRow';
import type { LeaderboardTeamVisual } from './LeaderboardCard.constants';
import { LeaderboardScrollBody } from './LeaderboardScrollBody';
import type { LeaderboardEntry, WorldCupGroupCompanyReward } from '../../../src/worldcup/types/worldcup.types';
import type { WorldCupFuturesOptions } from '../../../src/worldcup/helpers/worldcupPicksData.helper';

export { LeaderboardUserAvatar } from './LeaderboardUserAvatar';

export type WorldCupLeaderboardLayout = 'flashlist' | 'scroll';

/**
 * Props shared by both layouts. Layout-specific props live on the discriminated
 * branches below ({@link WorldCupLeaderboardProps}).
 */
interface WorldCupLeaderboardSharedProps {
  top3: LeaderboardEntry[];
  rest: LeaderboardEntry[];
  /**
   * When provided, this entry is rendered at the bottom of the list with a divider above it.
   * Set when the API returns the current user separately (e.g. not in the top-50 global slice).
   */
  pinnedCurrentUser?: LeaderboardEntry;
  /**
   * When true, podium cards belonging to the current user get the "me" highlight
   * border/background. Used by GroupDetailView.
   */
  showCurrentUserPodiumVariant?: boolean;
  /**
   * Glass card list (no podium), merged ranks 1+.
   * Column headers are rendered by the parent above the list.
   */
  leaderboardGlass?: boolean;
  onLeaderboardRowPress?: (entry: LeaderboardEntry) => void;
  /** From GET …/matches `futuresOptions` — maps opaque winner/scorer IDs on each row. */
  futuresOptions?: WorldCupFuturesOptions | null;
  /**
   * Flags (national teams) or kits (clubs). Defaults to `'flag'` so every existing caller —
   * the World Cup board, squad standings, group detail — is unchanged by omission (AC-3.6).
   */
  teamVisual?: LeaderboardTeamVisual;
  /**
   * When provided, each glass card shows a 3-dot menu button and calls this
   * when tapped. The caller measures via the `menuTriggerRef` setter.
   */
  onOpenRowMenu?: (entry: LeaderboardEntry) => void;
  /** Receives per-row trigger refs keyed by entry.id so callers can measure. */
  registerMenuTriggerRef?: (entryId: string, ref: View | null) => void;
  /** Optional content rendered directly below the podium and above the row list (e.g. search). */
  belowPodium?: React.ReactNode;
  /** Optional content rendered at the very top of the list, above the podium (e.g. a section header). */
  listTopContent?: React.ReactNode;
  /** Optional content rendered after the ranked rows and before the pinned footer (e.g. a "Show more" control). */
  listBottomContent?: React.ReactNode;
  /**
   * Whether to render the Week/Overall toggle on the podium. Defaults to false —
   * only group views surface it; global leaderboard is overall-only.
   */
  showPeriodToggle?: boolean;
  /** Selected leaderboard period — required when {@link showPeriodToggle} is true. */
  period?: LeaderboardPeriod;
  onPeriodChange?: (next: LeaderboardPeriod) => void;
  /**
   * When true, the section below the toggle (podium avatars + ranked rows) renders
   * a skeleton instead of real data. Used on Week ↔ Overall toggle: the page chrome
   * (hero banner, toggle, podium blocks) stays visible while new-period rows load.
   */
  isLeaderboardLoading?: boolean;
  /** Company group rewards — passed through to the podium to show prizes below each block. */
  rewards?: WorldCupGroupCompanyReward[] | null;
  /** When true, skips podium stage and belowPodium content (e.g. squad TV display mode). */
  hidePodiumStage?: boolean;
  /** Skips StaggeredFadeIn on rows (TV / large lists). */
  disableRowEntranceAnimation?: boolean;
}

interface WorldCupLeaderboardFlashListProps extends WorldCupLeaderboardSharedProps {
  layout?: 'flashlist';
  /** Passed as contentContainerStyle to the internal FlashList. */
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Called whenever the current user's row enters or leaves the visible viewport. */
  onCurrentUserVisibilityChanged?: (isVisible: boolean) => void;
  /** Exposes the FlashList ref for imperative scrollToIndex. */
  flashListRef?: React.RefObject<FlashListRef<LeaderboardEntry> | null>;
  scrollEnabled?: boolean;
  onContentSizeChange?: (width: number, height: number) => void;
  /** Auto-load the next page when the list nears its end (infinite squad leaderboard). */
  onEndReached?: () => void;
  /** FlashList onEndReached trigger distance (fraction of visible length). Defaults to 0.5. */
  onEndReachedThreshold?: number;
}

interface WorldCupLeaderboardScrollProps extends WorldCupLeaderboardSharedProps {
  layout: 'scroll';
  /**
   * Called with the View ref for the current user's row so the parent can track
   * its on-screen position via measureInWindow / measureLayout.
   */
  onCurrentUserRowRef?: (ref: View | null) => void;
}

export type WorldCupLeaderboardProps =
  | WorldCupLeaderboardFlashListProps
  | WorldCupLeaderboardScrollProps;

export const WorldCupLeaderboard: React.FC<WorldCupLeaderboardProps> = (props) => {
  const {
    top3,
    rest,
    pinnedCurrentUser,
    showCurrentUserPodiumVariant,
    leaderboardGlass = false,
    onLeaderboardRowPress,
    futuresOptions = null,
    teamVisual = 'flag',
    onOpenRowMenu,
    registerMenuTriggerRef,
    belowPodium,
    listTopContent,
    listBottomContent,
    showPeriodToggle = false,
    period = 'overall',
    onPeriodChange,
    isLeaderboardLoading = false,
    rewards = null,
    hidePodiumStage = false,
    disableRowEntranceAnimation = false,
  } = props;

  const theme = useTheme();
  const styles = useMemo(() => getWorldCupLeaderboardStyles(theme), [theme]);
  const futuresOpts = futuresOptions ?? null;

  // Narrow on `props.layout` rather than a local default — TypeScript only
  // discriminates against the original property access, not aliased locals.
  const isScrollLayout = props.layout === 'scroll';
  const onCurrentUserRowRef = props.layout === 'scroll' ? props.onCurrentUserRowRef : undefined;
  const onCurrentUserVisibilityChanged =
    props.layout === 'scroll' ? undefined : props.onCurrentUserVisibilityChanged;
  const flashListRef = props.layout === 'scroll' ? undefined : props.flashListRef;
  const contentContainerStyle =
    props.layout === 'scroll' ? undefined : props.contentContainerStyle;
  const scrollEnabled = props.layout === 'scroll' ? true : (props.scrollEnabled ?? true);
  const onContentSizeChange =
    props.layout === 'scroll' ? undefined : props.onContentSizeChange;
  const onEndReached = props.layout === 'scroll' ? undefined : props.onEndReached;
  const onEndReachedThreshold =
    props.layout === 'scroll' ? undefined : props.onEndReachedThreshold;

  // Keep the latest callback in a ref so viewabilityConfigCallbackPairs (a stable ref) can call it
  const currentUserVisibilityChangedRef = useRef(onCurrentUserVisibilityChanged);
  useEffect(() => {
    currentUserVisibilityChangedRef.current = onCurrentUserVisibilityChanged;
  }, [onCurrentUserVisibilityChanged]);

  const viewabilityConfigCallbackPairs = useRef([
    {
      viewabilityConfig: { itemVisiblePercentThreshold: 50 },
      onViewableItemsChanged: ({ viewableItems }: { viewableItems: ViewToken<LeaderboardEntry>[] }) => {
        const isVisible = viewableItems.some((vi) => vi.item?.isCurrentUser);
        currentUserVisibilityChangedRef.current?.(isVisible);
      },
    },
  ]);

  /**
   * Glass list shows every rank (1..N) — top-3 also appear on the podium stage above
   * for visual emphasis, but their row form lives inline with everyone else so live
   * predictions stay on the leaderboard rows instead of crowding the podium.
   */
  const glassList = useMemo(
    () => [...top3, ...rest].sort((a, b) => a.rank - b.rank),
    [top3, rest],
  );

  /**
   * Factory: returns a stable per-row ref setter so the inline arrow allocation
   * inside `glassList.map(...)` is moved out of render.
   */
  const makeRegisterRowMenuRef = useCallback(
    (entryId: string) =>
      (ref: View | null) => {
        registerMenuTriggerRef?.(entryId, ref);
      },
    [registerMenuTriggerRef],
  );

  // Podium keeps showing the previous period's top-3 during a Week ↔ Overall swap
  // (`keepPreviousData` in the query holds them in `top3`). Blanking the podium
  // here would collapse `playersRow` to 0 height and shift the rows below up,
  // jumping the user's scroll position. Only the ranked rows below swap to a
  // skeleton — see the skeleton branch in LeaderboardScrollBody.
  const ListHeader = useMemo(
    () => (
      <>
        {listTopContent}
        {hidePodiumStage ? null : (
          <>
            <PodiumStage
              top3={top3}
              period={period}
              onPeriodChange={onPeriodChange}
              showPeriodToggle={showPeriodToggle}
              showCurrentUserVariant={showCurrentUserPodiumVariant}
              onPressPlayer={onLeaderboardRowPress}
              rewards={rewards}
            />
            {belowPodium}
            <View style={styles.divider} />
          </>
        )}
      </>
    ),
    [hidePodiumStage, top3, period, onPeriodChange, showPeriodToggle, styles, showCurrentUserPodiumVariant, onLeaderboardRowPress, belowPodium, listTopContent, rewards],
  );

  const ListFooter = useMemo(() => {
    const footerPad = isScrollLayout ? styles.listFooterEmbedded : styles.listFooter;
    if (!pinnedCurrentUser) {
      return <View style={footerPad} />;
    }
    return (
      <View style={footerPad}>
        <View style={styles.pinnedDivider} />
        {leaderboardGlass ? (
          <LeaderboardCard
            entry={pinnedCurrentUser}
            onPressRow={onLeaderboardRowPress}
            futuresOptions={futuresOpts}
            teamVisual={teamVisual}
          />
        ) : (
          <LeaderboardRow
            entry={pinnedCurrentUser}
            styles={styles}
            futuresOptions={futuresOpts}
            teamVisual={teamVisual}
          />
        )}
      </View>
    );
  }, [pinnedCurrentUser, styles, isScrollLayout, leaderboardGlass, onLeaderboardRowPress, futuresOpts, teamVisual]);

  const ItemSeparator = useCallback(() => <View style={styles.separator} />, [styles.separator]);

  const renderItem = useCallback(
    ({ item, index }: { item: LeaderboardEntry; index: number }) => {
      const row = leaderboardGlass ? (
        <LeaderboardCard
          entry={item}
          onPressRow={onLeaderboardRowPress}
          futuresOptions={futuresOpts}
          onOpenMenu={onOpenRowMenu}
          menuTriggerRef={registerMenuTriggerRef ? makeRegisterRowMenuRef(item.id) : undefined}
          teamVisual={teamVisual}
        />
      ) : (
        <LeaderboardRow
          entry={item}
          styles={styles}
          futuresOptions={futuresOpts}
          teamVisual={teamVisual}
        />
      );
      if (disableRowEntranceAnimation) {
        return row;
      }
      return <StaggeredFadeIn index={index}>{row}</StaggeredFadeIn>;
    },
    [
      leaderboardGlass,
      futuresOpts,
      onLeaderboardRowPress,
      styles,
      disableRowEntranceAnimation,
      onOpenRowMenu,
      registerMenuTriggerRef,
      makeRegisterRowMenuRef,
      teamVisual,
    ],
  );

  if (isScrollLayout) {
    return (
      <LeaderboardScrollBody
        styles={styles}
        rest={rest}
        glassList={glassList}
        leaderboardGlass={leaderboardGlass}
        isLeaderboardLoading={isLeaderboardLoading}
        listHeader={ListHeader}
        listFooter={ListFooter}
        listBottomContent={listBottomContent}
        futuresOptions={futuresOpts}
        teamVisual={teamVisual}
        onLeaderboardRowPress={onLeaderboardRowPress}
        onOpenRowMenu={onOpenRowMenu}
        registerMenuTriggerRef={registerMenuTriggerRef}
        makeRegisterRowMenuRef={makeRegisterRowMenuRef}
        onCurrentUserRowRef={onCurrentUserRowRef}
      />
    );
  }

  // During a Week ↔ Overall swap the section shows a skeleton (matching the scroll
  // layout) while keeping the header chrome — empty the data so ListEmptyComponent
  // renders the skeleton rows below the still-mounted podium/header.
  const flashData = isLeaderboardLoading ? EMPTY_LEADERBOARD_DATA : leaderboardGlass ? glassList : rest;

  // "Show more" control (listBottomContent) sits above the pinned-current-user footer.
  const ListFooterWithBottomContent = (
    <>
      {listBottomContent}
      {ListFooter}
    </>
  );

  return (
    <FlashList
      ref={flashListRef}
      overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
      style={styles.flashList}
      data={flashData}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      ItemSeparatorComponent={leaderboardGlass ? undefined : ItemSeparator}
      ListHeaderComponent={ListHeader}
      ListEmptyComponent={isLeaderboardLoading ? <RowsSkeleton styles={styles} /> : undefined}
      ListFooterComponent={ListFooterWithBottomContent}
      contentContainerStyle={contentContainerStyle}
      showsVerticalScrollIndicator={false}
      scrollEnabled={scrollEnabled}
      onContentSizeChange={onContentSizeChange}
      onEndReached={onEndReached}
      onEndReachedThreshold={onEndReachedThreshold ?? 0.5}
      drawDistance={500}
      viewabilityConfigCallbackPairs={
        onCurrentUserVisibilityChanged
          ? viewabilityConfigCallbackPairs.current
          : undefined
      }
    />
  );
};

/** Stable empty-data reference so the skeleton branch doesn't churn FlashList data identity. */
const EMPTY_LEADERBOARD_DATA: LeaderboardEntry[] = [];

function RowsSkeleton({ styles }: { styles: ReturnType<typeof getWorldCupLeaderboardStyles> }) {
  return (
    <View style={styles.rowsSkeletonContainer}>
      <Skeleton width="100%" height={56} borderRadius={16} />
      <Skeleton width="100%" height={56} borderRadius={16} />
      <Skeleton width="100%" height={56} borderRadius={16} />
      <Skeleton width="100%" height={56} borderRadius={16} />
    </View>
  );
}
